import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Outlet, useLocation, useOutletContext } from 'react-router-dom';
import { toEsPath, toTsdbHref } from './tsdbPaths';
import { useTsdbStore } from './tsdbStore';

const TsdbBadge = (): JSX.Element => {
  const location = useLocation();
  const served = useTsdbStore((state) => state.served);
  const [open, setOpen] = useState(false);
  const endpoints = Object.entries(served).sort(([a], [b]) => a.localeCompare(b));

  return (
    <div className="fixed bottom-3 left-3 z-[9999] text-xs text-white">
      {open && (
        <div className="mb-2 w-80 rounded-lg bg-neutral-900/95 p-3 shadow-lg">
          <p className="mb-2 font-semibold">
            Elasticsearch data on this page comes from TimescaleDB
          </p>
          <p className="mb-2 text-amber-300">
            Only reads are redirected: actions such as dispatch still change the real system.
          </p>
          <ul className="mb-3 max-h-60 space-y-1 overflow-y-auto font-mono">
            {endpoints.length === 0 && (
              <li className="text-neutral-400">No copied endpoint called yet</li>
            )}
            {endpoints.map(([endpoint, count]) => (
              <li key={endpoint} className="flex justify-between gap-2">
                <span className="truncate">/api/dsh/tsdb{endpoint}</span>
                <span className="text-neutral-400">{count}</span>
              </li>
            ))}
          </ul>
          {/* Full page load, so the router keeps no TSDB session */}
          <a className="underline" href={`${toEsPath(location.pathname)}${location.search}`}>
            Open the Elasticsearch version
          </a>
        </div>
      )}
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="rounded-full bg-emerald-700 px-3 py-1.5 font-semibold shadow-lg"
      >
        TimescaleDB ({endpoints.length})
      </button>
    </div>
  );
};

const TsdbLayout = (): JSX.Element => {
  // Own cache: the copies share query keys with the Elasticsearch pages.
  const [queryClient] = useState(() => new QueryClient());
  const context = useOutletContext();

  // Pages open some copied pages (e.g. event details) in a popup, outside the router.
  useEffect(() => {
    const open = window.open;
    window.open = ((url, target, features) =>
      open.call(
        window,
        url === undefined ? url : toTsdbHref(String(url)),
        target,
        features
      )) as typeof window.open;
    return () => {
      window.open = open;
    };
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <Outlet context={context} />
      <TsdbBadge />
    </QueryClientProvider>
  );
};

export default TsdbLayout;
